/**
 * Kiosk Footer Component
 * Displays animated payment method icons
 * Based on Figma footer design
 */

import React, { useState, useEffect } from 'react';
import Lottie from 'lottie-react';
import { KioskUICustomization } from '../../types/kiosk-types';

interface KioskFooterProps {
  config: KioskUICustomization;
  size: 'small' | 'large';
}

/**
 * Helper function to detect if the theme is light based on background color
 * Converts hex color to RGB and calculates luminance
 */
function isLightTheme(hexColor: string): boolean {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate relative luminance (WCAG formula)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // If luminance > 0.5, it's a light background
  return luminance > 0.5;
}

/**
 * Kiosk Footer Component
 * Shows animated payment method icons (Visa, Mastercard, Apple Pay, Google Pay)
 * Uses Lottie animation for dynamic display
 * Automatically selects correct animation variant based on size and theme
 */
export function KioskFooter({ config, size }: KioskFooterProps) {
  const isSmall = size === 'small';
  const [animationData, setAnimationData] = useState<any>(null);
  const [error, setError] = useState(false);

  // Footer dimensions based on Figma design
  // 5" scaled: 90px -> 52px (0.583x)
  const footerHeight = isSmall ? '60px' : '52px';

  // Detect if theme is light or dark based on background color
  const isLight = isLightTheme(config.theme.colors.primaryBackground);

  // Select appropriate animation file based on size and theme
  const getAnimationPath = () => {
    if (isSmall) {
      // 4-inch display (480x76px)
      return isLight
        ? '/animations/payment_methods_small_light.json'
        : '/animations/payment_methods_small.json';
    } else {
      // 5-inch display (720x114px)
      return isLight
        ? '/animations/payment_methods_light.json'
        : '/animations/payment_methods.json';
    }
  };

  // Load Lottie animation when size or theme changes
  useEffect(() => {
    const animationPath = getAnimationPath();

    fetch(animationPath)
      .then((res) => res.json())
      .then((data) => setAnimationData(data))
      .catch((err) => {
        console.error('Failed to load payment animation:', err);
        setError(true);
      });
  }, [isSmall, isLight]);

  return (
    <div
      className="relative flex shrink-0 items-center justify-center overflow-hidden"
      style={{
        height: footerHeight,
        backgroundColor: config.theme.colors.primaryBackground,
        borderTop: `1px solid ${config.theme.colors.border}`,
        zIndex: 10,
      }}
    >
      {/* Lottie payment animation or fallback to static image */}
      {animationData && !error ? (
        <Lottie
          animationData={animationData}
          loop={true}
          autoplay={true}
          style={{
            height: footerHeight,
            width: 'auto',
          }}
        />
      ) : (
        <img
          src="/figma-assets/kiosk/payment-logos.svg"
          alt="Payment methods: Visa, Mastercard, Apple Pay, Google Pay"
          style={{
            height: isSmall ? '40px' : '35px',
            width: 'auto',
            objectFit: 'contain',
          }}
        />
      )}
    </div>
  );
}
